export type GlossaryCategoryKey = "rates" | "valuation" | "quality" | "cashflow" | "etf" | "workflow";

export type GlossaryEntry = {
  slug: string;
  label: string;
  category: GlossaryCategoryKey;
  shortDefinition: string;
  explanation: string;
  formula?: string;
  interpretation?: string;
  houseRule?: string;
  aliases?: string[];
  related?: string[];
};

export const GLOSSARY_CATEGORIES: Array<{ key: GlossaryCategoryKey; label: string; }> = [
  { key: "rates", label: "Rates" },
  { key: "valuation", label: "Valuation" },
  { key: "quality", label: "Quality & Moat" },
  { key: "cashflow", label: "Cash Flow & Balance Sheet" },
  { key: "etf", label: "ETF" },
  { key: "workflow", label: "Workflow" },
];

const glossaryEntries: GlossaryEntry[] = [
  {
    slug: "house-hurdle",
    label: "House Hurdle",
    category: "valuation",
    shortDefinition: "The house rate-aware multiple threshold, kept independent from peers and DCF scenarios.",
    explanation: "The House Hurdle compares the company's cash-flow yield with Treasury alternatives and applies the configured EV/FCF cap. It is a mechanical underwriting constraint rather than an intrinsic-value estimate.",
    interpretation: "Use it to test whether the current price clears the house return requirement.",
    related: ["fcf-10y", "dcf-lab", "trading-comparables"],
  },
  {
    slug: "trading-comparables",
    label: "Trading Comparables",
    category: "valuation",
    shortDefinition: "A context-only comparison of the subject's valuation multiples with selected public peers.",
    explanation: "Trading comparables use cached peer financials and market values. The system prefers industry peers, requires at least three valid observations for quartiles, and reconciles enterprise-value multiples through net debt before estimating equity value.",
    interpretation: "Peer ranges describe how public companies trade; they are not fair value, transaction multiples, or a recommendation.",
    houseRule: "Comparable outputs never change the V2 score, confidence, screening disposition, or candidate gates.",
    related: ["house-hurdle", "dcf-lab", "ev-fcf"],
  },
  {
    slug: "rates-baseline",
    label: "Rates Baseline",
    category: "rates",
    shortDefinition: "The current Treasury and policy-rate backdrop used as an equity hurdle layer.",
    explanation: "Rates Baseline is the macro context layer for the Investments workflow. It summarizes short-rate, curve, and spread context so equity ideas can be judged against real Treasury alternatives rather than in isolation.",
    interpretation: "It should improve equity discipline. It is not a direct stocks-vs-bonds allocation engine.",
    houseRule: "The app uses rates as a hurdle comparison for equities. It does not automatically change valuation outputs or make bond allocation recommendations.",
    related: ["policy-target", "3m-bill", "10y-note", "curve-regime", "fcf-yield", "fcf-10y"],
  },
  {
    slug: "policy-target",
    label: "Policy Target",
    category: "rates",
    shortDefinition: "The Federal Reserve's current target range for the federal funds rate.",
    explanation: "This is the policy anchor for short-term U.S. dollar rates and the starting point for interpreting the rest of the curve.",
    interpretation: "Higher policy ranges raise the hurdle for fresh equity ideas versus cash-like alternatives.",
    related: ["effr", "3m-bill", "rates-baseline"],
  },
  {
    slug: "effr",
    label: "EFFR",
    category: "rates",
    shortDefinition: "The Effective Federal Funds Rate is the actual overnight interbank lending rate.",
    explanation: "EFFR is the market rate traded between depository institutions and usually sits inside the Fed's target range.",
    interpretation: "It is the cleanest read on where overnight policy-sensitive funding is really trading.",
    related: ["policy-target"],
  },
  {
    slug: "3m-bill",
    label: "3M Bill",
    category: "rates",
    shortDefinition: "The 3-month Treasury bill yield is the near-cash hurdle rate.",
    explanation: "This is the most practical benchmark for cash-like alternatives inside the app's equity workflow.",
    interpretation: "If a stock cannot comfortably clear this hurdle on cash yield, the idea needs stronger growth or a better entry price.",
    related: ["policy-target", "fcf-3m", "beats-3m", "rate-hurdle"],
  },
  {
    slug: "2y-note",
    label: "2Y Note",
    category: "rates",
    shortDefinition: "The 2-year Treasury yield reflects policy expectations over the next several meetings.",
    explanation: "The 2-year part of the curve often moves closely with repricing around Federal Reserve expectations.",
    related: ["2s10s", "policy-target"],
  },
  {
    slug: "10y-note",
    label: "10Y Note",
    category: "rates",
    shortDefinition: "The 10-year Treasury yield is the standard long-duration hurdle rate.",
    explanation: "This is the long-duration Treasury benchmark used in the app when comparing equity cash yields to safer long-horizon alternatives.",
    interpretation: "As equity cash yields compress toward the 10Y, the margin for error gets thinner.",
    related: ["fcf-10y", "buy-yld-10y", "beats-10y", "curve-regime"],
  },
  {
    slug: "30y-bond",
    label: "30Y Bond",
    category: "rates",
    shortDefinition: "The 30-year Treasury yield represents the far end of the nominal Treasury curve.",
    explanation: "It reflects very long-duration inflation and discount-rate expectations.",
    related: ["treasury-curve"],
  },
  {
    slug: "2s10s",
    label: "2s10s",
    category: "rates",
    shortDefinition: "The spread between the 2-year and 10-year Treasury yields.",
    explanation: "This curve measure highlights whether the front end is above or below the long end of the Treasury curve.",
    formula: "2s10s = 2Y Note Yield - 10Y Note Yield",
    interpretation: "Negative values mean inversion. Positive values mean a more normal upward slope.",
    related: ["2y-note", "10y-note", "curve-regime", "treasury-curve"],
  },
  {
    slug: "3m-10y",
    label: "3M-10Y",
    category: "rates",
    shortDefinition: "The spread between the 3-month bill yield and the 10-year Treasury yield.",
    explanation: "This is a widely watched curve-shape measure and recession-context signal.",
    formula: "3M-10Y = 3M Bill Yield - 10Y Note Yield",
    interpretation: "Negative values indicate an inverted curve under the app's label convention.",
    related: ["3m-bill", "10y-note", "curve-regime", "treasury-curve"],
  },
  {
    slug: "curve-regime",
    label: "Curve Regime",
    category: "rates",
    shortDefinition: "A simple classification of the Treasury curve as inverted, flat, or normal.",
    explanation: "The app uses the key curve spreads to summarize whether the term structure is offering healthy slope or signaling pressure.",
    interpretation: "It is macro context, not a stand-alone stock recommendation.",
    houseRule: "The regime is a compact readout of spread conditions. It is not a recession forecast or trading signal by itself.",
    related: ["2s10s", "3m-10y", "rates-baseline"],
  },
  {
    slug: "treasury-curve",
    label: "Treasury Curve",
    category: "rates",
    shortDefinition: "The current set of Treasury yields across bills, notes, and bonds.",
    explanation: "The curve shows how nominal Treasury yields change across maturities, from near-cash bills to long bonds.",
    interpretation: "Its shape provides context for discount rates, funding pressure, and the opportunity cost of taking equity risk.",
    related: ["2s10s", "3m-10y", "curve-regime"],
  },
  {
    slug: "rate-hurdle",
    label: "Rate Hurdle",
    category: "rates",
    shortDefinition: "A filter that compares equity cash-yield metrics against Treasury benchmarks.",
    explanation: "The Research screen uses rate-hurdle filters to show which equities currently beat or fall below the 3M bill or 10Y Treasury.",
    interpretation: "This is a screening aid, not a complete valuation model.",
    related: ["3m-bill", "10y-note", "fcf-3m", "fcf-10y", "beats-3m", "beats-10y"],
  },
  {
    slug: "market-cap",
    label: "Market Cap",
    category: "valuation",
    shortDefinition: "The equity market value of the common shares.",
    explanation: "Market capitalization reflects what public investors currently pay for the equity slice of the business.",
    formula: "Market Cap = Share Price × Diluted Shares Outstanding",
    related: ["enterprise-value"],
  },
  {
    slug: "enterprise-value",
    label: "Enterprise Value",
    category: "valuation",
    shortDefinition: "A total-business valuation proxy that combines equity value and net debt.",
    explanation: "Enterprise Value is used in the app to compare the price of the business to operating or cash metrics without letting capital structure dominate the comparison.",
    formula: "Enterprise Value = Market Cap + Total Debt - Cash",
    houseRule: "This app uses a simplified EV formula and intentionally does not model every balance-sheet adjustment.",
    related: ["ev-fcf-ttm", "ev-ebitda-ttm", "market-cap"],
  },
  {
    slug: "pe-ttm",
    label: "P/E (TTM)",
    category: "valuation",
    shortDefinition: "Price-to-earnings on trailing 12-month earnings.",
    explanation: "This is the common equity valuation multiple based on trailing net income per share.",
    aliases: ["P/E"],
    related: ["valuation-zone"],
  },
  {
    slug: "pfcf-ttm",
    label: "P/FCF (TTM)",
    category: "valuation",
    shortDefinition: "Price-to-free-cash-flow on trailing 12-month cash generation.",
    explanation: "This multiple values the equity against free cash flow attributable to shareholders.",
    aliases: ["P/FCF"],
    related: ["fcf-ttm", "fcf-yield"],
  },
  {
    slug: "ev-fcf-ttm",
    label: "EV/FCF (TTM)",
    category: "valuation",
    shortDefinition: "Enterprise value divided by trailing 12-month free cash flow.",
    explanation: "This is the app's main cash-valuation reference because it compares total business value to free cash flow while staying more neutral to capital structure than P/FCF.",
    formula: "EV/FCF = Enterprise Value / Free Cash Flow",
    interpretation: "Lower multiples imply higher cash yield if the underlying cash flow is durable.",
    houseRule: "EV/FCF is the primary valuation multiple in the house overlay and buy-level framework.",
    aliases: ["EV/FCF", "Current EV/FCF", "Current EV / FCF"],
    related: ["enterprise-value", "fcf-ttm", "fcf-yield", "valuation-zone", "current-zone"],
  },
  {
    slug: "ev-ebitda-ttm",
    label: "EV/EBITDA (TTM)",
    category: "valuation",
    shortDefinition: "Enterprise value divided by trailing EBITDA.",
    explanation: "This multiple benchmarks total-business value against a pre-interest, pre-tax, pre-D&A operating earnings measure.",
    aliases: ["EV/EBITDA"],
    related: ["enterprise-value"],
  },
  {
    slug: "current-zone",
    label: "Current Zone",
    category: "valuation",
    shortDefinition: "The current buy-price-overlay bucket implied by the stock's present EV/FCF multiple.",
    explanation: "Current Zone is the live valuation bucket inside the buy-level overlay. It summarizes where the current EV/FCF sits relative to the house entry bands.",
    interpretation: "It is a current-state discipline readout, not a full investment decision by itself.",
    houseRule: "The zone comes from the house buy-price overlay rather than a textbook market standard.",
    aliases: ["Zone", "Valuation Now"],
    related: ["ev-fcf-ttm", "valuation-zone", "buy-tier"],
  },
  {
    slug: "valuation-zone",
    label: "Valuation Zone",
    category: "valuation",
    shortDefinition: "The house valuation bucket used in action mapping and price discipline.",
    explanation: "The app groups the current valuation setup into buckets like BUY, HOLD, WATCHLIST, AVOID, or UNAVAILABLE based on the configured overlay logic.",
    interpretation: "It is a house framework output, not a universal market standard.",
    houseRule: "The thresholds behind the valuation zone are explicit house policy, not textbook finance formulas.",
    aliases: ["Valuation", "Current Zone"],
    related: ["final-action", "buy-tier", "ev-fcf-ttm"],
  },
  {
    slug: "final-action",
    label: "Final Action",
    category: "valuation",
    shortDefinition: "The house recommendation after combining quality, moat, valuation, and discipline outputs.",
    explanation: "Final Action is the top-line decision label shown across Coverage, Command, and detail views.",
    interpretation: "It compresses multiple model layers into one action, so users should still inspect the underlying drivers.",
    houseRule: "This is a house decision rule, not an external analyst rating or a broker recommendation.",
    aliases: ["Resolved Action", "Action", "Engine Action", "House Action"],
    related: ["business-quality-score", "moat-proxy-score", "valuation-zone", "score"],
  },
  {
    slug: "buy-tier",
    label: "Buy Tier",
    category: "valuation",
    shortDefinition: "The quality tier assigned to the buy-level framework.",
    explanation: "The tier summarizes how demanding the buy-level assumptions and overlay bands should be for the company.",
    interpretation: "Higher tiers usually demand stricter price discipline and better quality.",
    houseRule: "Buy Tier is a house valuation-framework classification, not an external industry standard.",
    related: ["valuation-zone", "current-zone"],
  },
  {
    slug: "current-price",
    label: "Current Price",
    category: "valuation",
    shortDefinition: "The latest price used as the anchor for entry and risk comparisons.",
    explanation: "Current Price is the live or derived share price the app compares against buy targets, watch targets, and fair value references.",
    interpretation: "It is the anchor for entry discipline, not a recommendation by itself.",
    aliases: ["Current Price (Derived)"],
    related: ["target-buy", "watch-target", "margin-of-safety"],
  },
  {
    slug: "weighted-avg-shares",
    label: "Weighted Avg Shares",
    category: "valuation",
    shortDefinition: "The diluted share count used for per-share valuation outputs.",
    explanation: "Weighted Avg Shares is the denominator used when the app turns equity value into a fair-value-per-share readout.",
    related: ["fair-value-per-share", "market-cap"],
  },
  {
    slug: "buy-levels",
    label: "Buy Levels",
    category: "workflow",
    shortDefinition: "The full house entry ladder derived from overlay and DCF context.",
    explanation: "Buy Levels is the house framework that surfaces strong-buy, buy, accumulate, and watch contexts from the configured price-discipline model.",
    related: ["entry-framework", "buy-tier", "target-buy", "watch-target"],
  },
  {
    slug: "margin-of-safety",
    label: "Margin of Safety",
    category: "valuation",
    shortDefinition: "The percent gap between current price and fair value.",
    explanation: "Margin of Safety measures how far current price sits below or above the app's fair-value reference.",
    formula: "Margin of Safety = (Fair Value - Current Price) / Current Price",
    interpretation: "Positive values imply price is below fair value. Negative values imply price is above fair value.",
    related: ["current-price", "fair-value-per-share", "valuation-state"],
  },
  {
    slug: "fair-value-per-share",
    label: "Fair Value / Share",
    category: "valuation",
    shortDefinition: "The per-share fair value implied by the selected scenario.",
    explanation: "Fair Value / Share turns the model's enterprise or equity value output into a comparable per-share figure for price-discipline decisions.",
    aliases: ["Fair Value (BASE)"],
    related: ["margin-of-safety", "current-price", "dcf-sanity-check"],
  },
  {
    slug: "valuation-state",
    label: "Valuation State",
    category: "valuation",
    shortDefinition: "A plain-English label for where current price sits versus fair value.",
    explanation: "Valuation State translates the margin-of-safety readout into a faster label such as Value, Fair, Rich, or Overpriced.",
    interpretation: "It is a framing shortcut, not a full investment decision.",
    related: ["margin-of-safety", "fair-value-gauge", "current-zone"],
  },
  {
    slug: "buy-threshold-yield",
    label: "Buy Threshold Yield",
    category: "valuation",
    shortDefinition: "The implied cash-yield hurdle the house framework wants at a buy price.",
    explanation: "Buy Threshold Yield converts the house buy multiple into a yield-equivalent hurdle so entry discipline can be compared against Treasury alternatives.",
    interpretation: "Higher required threshold yields mean stricter entry discipline.",
    related: ["buy-yld-10y", "fcf-yield", "target-buy"],
  },
  {
    slug: "fair-value-gauge",
    label: "Fair Value Gauge",
    category: "workflow",
    shortDefinition: "The compact visual readout of price versus fair value.",
    explanation: "Fair Value Gauge shows whether current price is below, near, or above the selected fair-value reference using the app's margin-of-safety framing.",
    houseRule: "This gauge is a display layer over the house fair-value and margin-of-safety calculation.",
    related: ["margin-of-safety", "valuation-state", "fair-value-per-share"],
  },
  {
    slug: "entry-discipline",
    label: "Entry",
    category: "workflow",
    shortDefinition: "The part of the detail view focused on whether the stock is buyable now.",
    explanation: "Entry brings together current price, targets, overlay context, and DCF sanity checks into one price-discipline surface.",
    interpretation: "It is a decision surface first, not a full model workbench.",
    related: ["entry-framework", "target-buy", "watch-target", "dcf-sanity-check"],
  },
  {
    slug: "entry-framework",
    label: "Entry Framework",
    category: "workflow",
    shortDefinition: "The house price-discipline workflow for deciding whether a name is buyable.",
    explanation: "Entry Framework combines the EV/FCF overlay, target prices, and a compact DCF cross-check so the app can separate buyable setups from watchlist setups.",
    houseRule: "The overlay leads. DCF is a sanity check rather than the primary recommendation engine.",
    related: ["ev-fcf-overlay", "target-buy", "watch-target", "dcf-sanity-check"],
  },
  {
    slug: "ev-fcf-overlay",
    label: "EV/FCF Overlay",
    category: "workflow",
    shortDefinition: "The house overlay that maps EV/FCF ranges into entry bands and target prices.",
    explanation: "This is the primary entry engine in the detail view. It converts the house EV/FCF discipline into buy, watch, and avoid contexts.",
    houseRule: "Static and Adjusted overlays are house variants of the same entry framework, not market-standard labels.",
    aliases: ["Static Overlay", "Adjusted Overlay", "House Mode", "EV/FCF Bands"],
    related: ["ev-fcf-ttm", "entry-framework", "target-buy", "watch-target"],
  },
  {
    slug: "dcf-horizon",
    label: "DCF Horizon",
    category: "workflow",
    shortDefinition: "The explicit forecast length used for the DCF sanity check or lab view.",
    explanation: "DCF Horizon controls whether the valuation sanity check uses the shorter or longer forecast period before terminal value takes over.",
    aliases: ["House Frame", "DCF Sanity Horizon", "Horizon"],
    related: ["dcf-sanity-check", "discount-rate", "terminal-growth"],
  },
  {
    slug: "buyability-now",
    label: "Buyability Now",
    category: "workflow",
    shortDefinition: "A summary of whether the current setup is close enough to buy under the house discipline.",
    explanation: "Buyability Now condenses the price-discipline question into buy target, watch target, and margin-of-safety language.",
    related: ["target-buy", "watch-target", "margin-of-safety", "entry-discipline"],
  },
  {
    slug: "assumptions",
    label: "Assumptions",
    category: "workflow",
    shortDefinition: "The compact list of model inputs behind the displayed entry or DCF view.",
    explanation: "Assumptions summarize the seed inputs and key rates that shape the fair-value outputs shown in the detail view.",
    related: ["growth-anchor", "discount-rate", "terminal-growth", "dcf-sanity-check"],
  },
  {
    slug: "dcf-sanity-check",
    label: "DCF Sanity Check",
    category: "workflow",
    shortDefinition: "The compact DCF cross-check used alongside the overlay-driven entry framework.",
    explanation: "DCF Sanity Check is a deliberately simplified readout that tests whether the current setup is directionally consistent with a base DCF view.",
    interpretation: "It should inform discipline, not override the overlay by itself.",
    related: ["entry-framework", "dcf-horizon", "fair-value-per-share"],
  },
  {
    slug: "growth-anchor",
    label: "Growth Anchor",
    category: "workflow",
    shortDefinition: "The baseline growth reference used to seed the displayed DCF context.",
    explanation: "Growth Anchor is the anchor rate that the app uses when building or interpreting the displayed DCF scenarios.",
    related: ["growth-source", "discount-rate", "terminal-growth"],
  },
  {
    slug: "growth-source",
    label: "Growth Source",
    category: "workflow",
    shortDefinition: "The source label for the growth anchor used in the displayed model.",
    explanation: "Growth Source explains where the displayed growth anchor came from, such as revenue margin blending or a normalized house assumption.",
    related: ["growth-anchor", "dcf-sanity-check"],
  },
  {
    slug: "growth-rate",
    label: "Growth Rate",
    category: "workflow",
    shortDefinition: "The annual free-cash-flow growth assumption used in a selected scenario.",
    explanation: "Growth Rate is the explicit forecast growth assumption inside the scenario table or reverse DCF diagnostics.",
    related: ["growth-anchor", "dcf-horizon", "terminal-growth"],
  },
  {
    slug: "discount-rate",
    label: "Discount Rate",
    category: "workflow",
    shortDefinition: "The required return used to discount future cash flows back to today.",
    explanation: "Discount Rate translates future cash flows into present value and is one of the strongest assumptions in any DCF output.",
    related: ["terminal-growth", "fair-value-per-share", "dcf-sanity-check"],
  },
  {
    slug: "terminal-growth",
    label: "Terminal Growth",
    category: "workflow",
    shortDefinition: "The perpetual growth assumption used after the explicit forecast period ends.",
    explanation: "Terminal Growth shapes the terminal value portion of the DCF and therefore has a large effect on fair value.",
    related: ["discount-rate", "fair-value-per-share", "dcf-horizon"],
  },
  {
    slug: "accumulate-threshold",
    label: "Accumulate Threshold",
    category: "workflow",
    shortDefinition: "The softer add-at-price level above buy and strong-buy thresholds.",
    explanation: "Accumulate Threshold is the highest rung on the entry ladder and marks the price area where the setup becomes more interesting without being a full buy.",
    related: ["target-buy", "watch-target", "fair-value-per-share"],
  },
  {
    slug: "fcf-yield",
    label: "FCF Yield",
    category: "valuation",
    shortDefinition: "The current free-cash-flow yield implied by EV/FCF.",
    explanation: "In the Research and Rates baseline views, current FCF yield is used as the main cash-yield comparison against Treasury benchmarks.",
    formula: "Current FCF Yield = (1 / EV/FCF) × 100",
    interpretation: "Higher values mean more free cash flow relative to current enterprise value.",
    houseRule: "The app only computes this yield when EV/FCF is finite and positive.",
    related: ["ev-fcf-ttm", "fcf-3m", "fcf-10y", "beats-3m", "beats-10y"],
  },
  {
    slug: "fcf-3m",
    label: "FCF-3M",
    category: "valuation",
    shortDefinition: "The spread between current equity FCF yield and the 3-month bill yield.",
    explanation: "This tells you whether the stock currently offers more or less cash yield than the clean near-cash Treasury alternative.",
    formula: "FCF-3M = Current FCF Yield - 3M Bill Yield",
    interpretation: "Positive means the stock clears the near-cash hurdle. Negative means it does not.",
    related: ["fcf-yield", "3m-bill", "rate-hurdle"],
  },
  {
    slug: "fcf-10y",
    label: "FCF-10Y",
    category: "valuation",
    shortDefinition: "The spread between current equity FCF yield and the 10-year Treasury yield.",
    explanation: "This is the long-duration hurdle comparison used in the equity-hurdle workflow.",
    formula: "FCF-10Y = Current FCF Yield - 10Y Note Yield",
    interpretation: "Positive means the stock's current cash yield is above the 10Y Treasury. Negative means the Treasury hurdle still dominates.",
    related: ["fcf-yield", "10y-note", "median-fcf-10y", "beats-10y"],
  },
  {
    slug: "buy-yld-10y",
    label: "Buy Yld-10Y",
    category: "valuation",
    shortDefinition: "The spread between the implied buy-threshold yield and the 10-year Treasury yield.",
    explanation: "This shows whether the app's buy threshold still demands a spread over the long Treasury benchmark.",
    formula: "Buy Yld-10Y = Buy Threshold Yield - 10Y Note Yield",
    interpretation: "Positive means the house entry discipline still requires an equity yield advantage over the 10Y.",
    houseRule: "The buy-threshold yield is derived from the app's buy multiple framework, not from a market convention.",
    aliases: ["Median Buy Yld-10Y"],
    related: ["buy-tier", "10y-note", "fcf-10y"],
  },
  {
    slug: "beats-3m",
    label: "Beats 3M",
    category: "valuation",
    shortDefinition: "A count of equities whose current FCF yield is above the 3-month bill yield.",
    explanation: "This summary metric shows how much of the tracked equity universe currently clears the near-cash Treasury hurdle.",
    formula: "Beats 3M if Current FCF Yield - 3M Bill Yield > 0",
    related: ["fcf-3m", "3m-bill", "rate-hurdle"],
  },
  {
    slug: "beats-10y",
    label: "Beats 10Y",
    category: "valuation",
    shortDefinition: "A count of equities whose current FCF yield is above the 10-year Treasury yield.",
    explanation: "This summary metric shows how much of the tracked equity universe currently clears the long-duration Treasury hurdle.",
    formula: "Beats 10Y if Current FCF Yield - 10Y Note Yield > 0",
    related: ["fcf-10y", "10y-note", "rate-hurdle"],
  },
  {
    slug: "median-fcf-10y",
    label: "Median FCF-10Y",
    category: "valuation",
    shortDefinition: "The median FCF-10Y spread across the tracked equity universe.",
    explanation: "This summarizes whether the universe is broadly offering a cash-yield cushion above or below the 10Y Treasury.",
    formula: "Median of valid FCF-10Y spreads across covered equities",
    related: ["fcf-10y", "pressured-families"],
  },
  {
    slug: "pressured-families",
    label: "Pressured Families",
    category: "valuation",
    shortDefinition: "Families whose median current FCF yield spread versus the 10Y is weakest.",
    explanation: "This highlights where the tracked equity universe looks most pressured by current Treasury hurdles.",
    interpretation: "It is a family-level pressure summary, not a stock-level buy or sell recommendation.",
    aliases: ["Most Pressured Families"],
    related: ["median-fcf-10y", "fcf-10y", "10y-note"],
  },
  {
    slug: "score",
    label: "Score",
    category: "valuation",
    shortDefinition: "The normalized house score out of 100.",
    explanation: "The displayed score compresses business quality, moat proxy, and valuation discipline into a single normalized number.",
    interpretation: "It is most useful when read alongside the action, valuation zone, and moat band instead of alone.",
    houseRule: "This is a house composite score, not an external standardized rating.",
    aliases: ["Normalized", "Score Used", "Overall Score"],
    related: ["business-quality-score", "moat-proxy-score", "final-action"],
  },
  {
    slug: "revenue-ttm",
    label: "Revenue TTM",
    category: "cashflow",
    shortDefinition: "Trailing 12-month revenue.",
    explanation: "Revenue TTM shows the current operating scale of the business using the latest rolling year of results.",
    formula: "Revenue TTM = Sum of the latest 4 quarters of revenue when quarterly data is available",
    related: ["revenue-yoy-ttm", "revenue-3y-cagr"],
  },
  {
    slug: "revenue-yoy-ttm",
    label: "Revenue YoY (TTM)",
    category: "cashflow",
    shortDefinition: "Year-over-year growth in trailing 12-month revenue.",
    explanation: "This tracks recent demand momentum using a rolling-year revenue comparison.",
    related: ["revenue-ttm", "revenue-3y-cagr"],
  },
  {
    slug: "revenue-3y-cagr",
    label: "Revenue 3Y CAGR",
    category: "cashflow",
    shortDefinition: "Three-year compounded revenue growth.",
    explanation: "This metric smooths short-term volatility and shows medium-term revenue consistency.",
    formula: "Revenue 3Y CAGR = (Ending Revenue / Starting Revenue)^(1/3) - 1",
    related: ["revenue-ttm", "revenue-yoy-ttm"],
  },
  {
    slug: "gross-margin",
    label: "Gross Margin",
    category: "quality",
    shortDefinition: "Gross profit as a share of revenue.",
    explanation: "Gross margin is a first-pass proxy for pricing power, mix quality, and unit economics.",
    formula: "Gross Margin = Gross Profit / Revenue",
    related: ["relative-gross-margin", "operating-margin"],
  },
  {
    slug: "relative-gross-margin",
    label: "Relative Gross Margin",
    category: "quality",
    shortDefinition: "Gross margin minus the industry median, in percentage points.",
    explanation: "This frames whether the company has a gross-profit advantage or disadvantage versus peers.",
    aliases: ["Industry Gross Margin"],
    related: ["gross-margin"],
  },
  {
    slug: "operating-margin",
    label: "Operating Margin",
    category: "quality",
    shortDefinition: "Operating income as a share of revenue.",
    explanation: "Operating margin shows how much profit is left after core operating costs.",
    formula: "Operating Margin = Operating Income / Revenue",
    related: ["relative-operating-margin", "gross-margin"],
  },
  {
    slug: "relative-operating-margin",
    label: "Relative Operating Margin",
    category: "quality",
    shortDefinition: "Operating margin minus the industry median, in percentage points.",
    explanation: "This highlights whether the business is operating more efficiently than peers.",
    aliases: ["Industry Operating Margin"],
    related: ["operating-margin"],
  },
  {
    slug: "fcf-margin",
    label: "FCF Margin",
    category: "cashflow",
    shortDefinition: "Free cash flow as a share of revenue.",
    explanation: "FCF margin measures how well reported revenue converts into post-reinvestment cash.",
    formula: "FCF Margin = Free Cash Flow / Revenue",
    aliases: ["Industry FCF Margin", "FCF Margin (TTM)"],
    related: ["fcf-ttm", "relative-fcf-margin"],
  },
  {
    slug: "relative-fcf-margin",
    label: "Relative FCF Margin",
    category: "cashflow",
    shortDefinition: "FCF margin minus the peer median, in percentage points.",
    explanation: "This shows whether the company converts revenue into free cash flow better or worse than peers.",
    related: ["fcf-margin"],
  },
  {
    slug: "cfo-ttm",
    label: "CFO (TTM)",
    category: "cashflow",
    shortDefinition: "Cash from operations over the trailing 12 months.",
    explanation: "CFO captures the core operating cash earning power of the business before capital spending.",
    aliases: ["CFO"],
    related: ["capex-ttm", "fcf-ttm"],
  },
  {
    slug: "capex-ttm",
    label: "CapEx (TTM)",
    category: "cashflow",
    shortDefinition: "Capital expenditure over the trailing 12 months.",
    explanation: "CapEx shows how much cash the company is reinvesting into physical or long-lived assets.",
    aliases: ["CapEx"],
    related: ["cfo-ttm", "fcf-ttm", "capex-cfo"],
  },
  {
    slug: "fcf-ttm",
    label: "FCF (TTM)",
    category: "cashflow",
    shortDefinition: "Free cash flow after operating cash flow and capital expenditure.",
    explanation: "FCF is the cash left after the business funds its reinvestment needs.",
    formula: "FCF = CFO + CapEx",
    houseRule: "The app uses FMP's sign convention, where CapEx is stored as a negative cash outflow. That makes CFO + CapEx equivalent to textbook CFO - CapEx.",
    aliases: ["FCF"],
    related: ["cfo-ttm", "capex-ttm", "fcf-margin", "fcf-yield"],
  },
  {
    slug: "roic-ttm",
    label: "ROIC (TTM)",
    category: "quality",
    shortDefinition: "Return on invested capital over the trailing 12 months.",
    explanation: "ROIC measures how efficiently the business turns deployed capital into after-tax operating earnings.",
    formula: "ROIC = NOPAT / Average Invested Capital",
    houseRule: "The app uses a simplified invested-capital proxy and documents that simplification explicitly in the engine notes.",
    aliases: ["ROIC"],
    related: ["roe-ttm", "roic-persistence"],
  },
  {
    slug: "roe-ttm",
    label: "ROE (TTM)",
    category: "quality",
    shortDefinition: "Return on equity over the trailing 12 months.",
    explanation: "ROE shows how much profitability the company generates on shareholder capital.",
    formula: "ROE = Net Income / Average Equity",
    aliases: ["ROE"],
    related: ["roic-ttm"],
  },
  {
    slug: "debt-fcf",
    label: "Debt / FCF",
    category: "cashflow",
    shortDefinition: "Total debt divided by free cash flow.",
    explanation: "This approximates how many years of current free cash flow it would take to repay debt.",
    formula: "Debt / FCF = Total Debt / Free Cash Flow",
    related: ["net-debt-discipline", "fcf-ttm"],
  },
  {
    slug: "net-debt",
    label: "Net Debt",
    category: "cashflow",
    shortDefinition: "Total debt minus cash and equivalents.",
    explanation: "Net Debt shows whether the business carries a true debt burden after cash is netted against borrowings.",
    formula: "Net Debt = Total Debt - Cash & Equivalents",
    related: ["debt-fcf", "enterprise-value"],
  },
  {
    slug: "current-ratio",
    label: "Current Ratio",
    category: "cashflow",
    shortDefinition: "Current assets divided by current liabilities.",
    explanation: "This is a simple short-term liquidity measure.",
    formula: "Current Ratio = Current Assets / Current Liabilities",
    related: ["debt-fcf"],
  },
  {
    slug: "business-quality-score",
    label: "Business Quality Score",
    category: "quality",
    shortDefinition: "Business-quality points out of 65.",
    explanation: "This score aggregates growth, margins, and balance-sheet strength into the app's quality framework.",
    interpretation: "It is meant to summarize business durability, not replace the underlying metrics.",
    houseRule: "The score is a house composite with explicit threshold ladders, not a standardized market metric.",
    aliases: ["Business Quality", "Business Score"],
    related: ["moat-proxy-score", "score", "final-action"],
  },
  {
    slug: "roic-persistence",
    label: "ROIC persistence",
    category: "quality",
    shortDefinition: "How consistently ROIC remains above threshold over time.",
    explanation: "This is used as a moat durability proxy inside the house scoring system.",
    houseRule: "Persistence is scored using explicit house thresholds rather than an external rating scale.",
    aliases: ["ROIC persistence points"],
    related: ["roic-ttm", "moat-proxy-score"],
  },
  {
    slug: "margin-stability",
    label: "Margin stability",
    category: "quality",
    shortDefinition: "A check on how much operating margins compress over time.",
    explanation: "The app uses margin stability as a proxy for pricing power and business resilience.",
    aliases: ["Margin stability points"],
    related: ["operating-margin", "moat-proxy-score"],
  },
  {
    slug: "revenue-durability-vs-peers",
    label: "Revenue durability vs peers",
    category: "quality",
    shortDefinition: "A measure of revenue resilience relative to peers.",
    explanation: "This asks whether the company sustains better growth than the broader industry comparison set.",
    aliases: ["Revenue durability points"],
    related: ["revenue-ttm", "moat-proxy-score"],
  },
  {
    slug: "reinvestment-efficiency",
    label: "Reinvestment efficiency",
    category: "quality",
    shortDefinition: "How well reinvestment translates into healthy free-cash-flow outcomes.",
    explanation: "The app uses this signal to distinguish productive reinvestment from capital-heavy pressure.",
    aliases: ["Reinvestment efficiency points"],
    related: ["capex-cfo", "fcf-margin", "moat-proxy-score"],
  },
  {
    slug: "moat-proxy-score",
    label: "Moat Proxy Score",
    category: "quality",
    shortDefinition: "Moat-proxy points out of 20.",
    explanation: "This score summarizes persistence, stability, durability, and reinvestment signals into one moat-style readout.",
    interpretation: "It is a quantitative durability proxy, not a claim that a company has a classic Buffett-style moat in every sense.",
    houseRule: "This is a house framework score with explicit point weights.",
    aliases: ["Moat Proxy", "Moat Score"],
    related: ["moat-band", "business-quality-score", "final-action"],
  },
  {
    slug: "moat-band",
    label: "Moat Band",
    category: "quality",
    shortDefinition: "The moat confidence classification used in the house framework.",
    explanation: "The band summarizes the confidence that the business shows durable qualitative strength in the quantitative proxy framework.",
    interpretation: "HIGH, MEDIUM, and FRAGILE are risk-framing labels, not absolute judgments.",
    houseRule: "Moat Band is a house classification derived from the moat proxy breakdown.",
    aliases: ["Moat", "Band"],
    related: ["moat-proxy-score", "final-action"],
  },
  {
    slug: "benchmarked-peers",
    label: "Benchmarked peers",
    category: "quality",
    shortDefinition: "The same-industry peer count used for benchmarking compared with total peers available.",
    explanation: "This signals how much peer context is available for relative margin and durability comparisons.",
    related: ["relative-gross-margin", "relative-operating-margin", "relative-fcf-margin"],
  },
  {
    slug: "capex-cfo",
    label: "CapEx / CFO",
    category: "cashflow",
    shortDefinition: "Capital spending as a percent of operating cash flow.",
    explanation: "This shows reinvestment pressure relative to current cash generation.",
    formula: "CapEx / CFO = Capital Expenditure / Cash From Operations",
    houseRule: "The app uses provider-normalized sign conventions internally but displays the ratio as a positive reinvestment burden measure.",
    related: ["capex-ttm", "cfo-ttm", "reinvestment-efficiency", "regime"],
  },
  {
    slug: "3y-trend",
    label: "3Y Trend",
    category: "quality",
    shortDefinition: "The three-year direction of the relevant metric, usually CapEx/CFO in the buy-level context.",
    explanation: "This is used to detect whether the business is structurally becoming more capital intensive or staying stable.",
    related: ["capex-cfo", "regime"],
  },
  {
    slug: "maintenance-like",
    label: "Maintenance-like",
    category: "quality",
    shortDefinition: "A signal that capital spending appears closer to maintenance than expansionary growth spend.",
    explanation: "This helps interpret whether reinvestment is simply maintaining the asset base or consuming increasing cash for growth.",
    related: ["capex-cfo", "regime"],
  },
  {
    slug: "growth-optional",
    label: "Growth-optional",
    category: "quality",
    shortDefinition: "A signal that reinvestment looks discretionary while free cash flow stays healthy.",
    explanation: "This supports the case that management has flexibility rather than being forced into heavy capital spending.",
    related: ["capex-cfo", "regime"],
  },
  {
    slug: "structurally-rising",
    label: "Structurally rising",
    category: "quality",
    shortDefinition: "A signal that reinvestment needs appear to be trending higher over time.",
    explanation: "This flags a business that may be moving into a more capital-hungry regime.",
    related: ["capex-cfo", "regime"],
  },
  {
    slug: "regime",
    label: "Regime",
    category: "quality",
    shortDefinition: "The app's free-cash-flow regime classification and penalty severity.",
    explanation: "This summarizes whether reinvestment and cash-conversion dynamics are supportive, neutral, or a drag on discipline scores.",
    houseRule: "Regime penalties are explicit house heuristics, not textbook formulas.",
    related: ["capex-cfo", "pre-penalty-score", "penalty", "adjusted-score"],
  },
  {
    slug: "dividend-payout-ratio",
    label: "Dividend payout ratio",
    category: "cashflow",
    shortDefinition: "The percentage of earnings paid out as dividends.",
    explanation: "This gives payout sustainability context and helps flag overly aggressive distributions.",
    related: ["buybacks-ttm"],
  },
  {
    slug: "buybacks-ttm",
    label: "Buybacks (TTM)",
    category: "cashflow",
    shortDefinition: "Trailing share repurchases over the last 12 months.",
    explanation: "This helps interpret whether management is actively shrinking the share count or prioritizing capital returns.",
    related: ["net-share-trend-3y", "net-share-cagr-3y"],
  },
  {
    slug: "net-share-trend-3y",
    label: "Net share trend (3Y)",
    category: "cashflow",
    shortDefinition: "The direction of share-count change over three years.",
    explanation: "This is a quick signal for dilution or buyback behavior.",
    related: ["net-share-cagr-3y", "buybacks-ttm"],
  },
  {
    slug: "net-share-cagr-3y",
    label: "Net share CAGR (3Y)",
    category: "cashflow",
    shortDefinition: "The compounded share-count change over three years.",
    explanation: "This quantifies the pace of dilution or repurchases.",
    related: ["net-share-trend-3y"],
  },
  {
    slug: "net-debt-discipline",
    label: "Net debt discipline",
    category: "cashflow",
    shortDefinition: "A quality check on debt posture using net debt and debt-to-FCF thresholds.",
    explanation: "This helps the app distinguish manageable leverage from balance-sheet pressure.",
    houseRule: "The discipline label is a house heuristic rather than a standardized credit rating.",
    related: ["debt-fcf", "enterprise-value"],
  },
  {
    slug: "pre-penalty-score",
    label: "Pre-penalty score",
    category: "valuation",
    shortDefinition: "The valuation-and-discipline score before regime penalties are applied.",
    explanation: "This is the starting score before the app reduces points for unfavorable free-cash-flow regime dynamics.",
    related: ["penalty", "adjusted-score", "regime"],
  },
  {
    slug: "penalty",
    label: "Penalty",
    category: "valuation",
    shortDefinition: "Points deducted because of unfavorable regime-shift dynamics.",
    explanation: "Penalties are used to avoid overrating businesses whose reinvestment or cash profile is deteriorating.",
    houseRule: "Penalty thresholds are explicit house rules.",
    related: ["pre-penalty-score", "adjusted-score", "regime"],
  },
  {
    slug: "adjusted-score",
    label: "Adjusted score",
    category: "valuation",
    shortDefinition: "The valuation-and-discipline score after penalties.",
    explanation: "This is the valuation-and-discipline component that actually flows into the displayed score and action mapping.",
    aliases: ["Valuation Score", "Valuation & Discipline"],
    related: ["pre-penalty-score", "penalty", "score"],
  },
  {
    slug: "etf-count",
    label: "ETF Count",
    category: "etf",
    shortDefinition: "The number of ETF rows currently shown in the coverage screen.",
    explanation: "This is a simple count of ETFs in the current filtered result set.",
    related: ["asset-class", "expense-ratio"],
  },
  {
    slug: "asset-class",
    label: "Asset Class",
    category: "etf",
    shortDefinition: "The ETF's broad exposure bucket, such as equity, fixed income, or alternatives.",
    explanation: "Asset class is the highest-level classification for what the fund owns or tracks.",
    aliases: ["Asset Classes"],
    related: ["etf-company", "holdings-count"],
  },
  {
    slug: "expense-ratio",
    label: "Expense Ratio",
    category: "etf",
    shortDefinition: "The annual fee charged by the ETF, expressed as a percentage of assets.",
    explanation: "Expense ratio is one of the first checks for whether the fund is cheap or expensive to hold structurally.",
    aliases: ["Median Expense Ratio"],
    related: ["assets-under-management", "etf-company"],
  },
  {
    slug: "assets-under-management",
    label: "Assets Under Management",
    category: "etf",
    shortDefinition: "The total assets managed by the fund.",
    explanation: "AUM is a quick proxy for fund scale and often for liquidity and survivability.",
    aliases: ["AUM", "Largest AUM"],
    related: ["avg-volume"],
  },
  {
    slug: "etf-company",
    label: "ETF Company",
    category: "etf",
    shortDefinition: "The sponsor or manager responsible for the ETF.",
    explanation: "This identifies the asset manager running the product.",
    related: ["asset-class", "expense-ratio"],
  },
  {
    slug: "nav",
    label: "NAV",
    category: "etf",
    shortDefinition: "Net asset value per share.",
    explanation: "NAV approximates the per-share value of the ETF's underlying holdings.",
    related: ["nav-currency"],
  },
  {
    slug: "nav-currency",
    label: "NAV Currency",
    category: "etf",
    shortDefinition: "The currency used for the ETF's reported net asset value.",
    explanation: "This matters when the fund or its holdings introduce currency considerations into the presentation of NAV.",
    related: ["nav", "domicile"],
  },
  {
    slug: "avg-volume",
    label: "Avg Volume",
    category: "etf",
    shortDefinition: "Average trading volume for the ETF.",
    explanation: "Average volume is a practical liquidity check for the fund's shares.",
    aliases: ["Average Volume"],
    related: ["assets-under-management"],
  },
  {
    slug: "holdings-count",
    label: "Holdings Count",
    category: "etf",
    shortDefinition: "The number of holdings in the fund.",
    explanation: "This gives a quick sense of concentration versus breadth.",
    aliases: ["Holdings"],
    related: ["sector-weights", "country-weights"],
  },
  {
    slug: "domicile",
    label: "Domicile",
    category: "etf",
    shortDefinition: "The legal home jurisdiction of the ETF.",
    explanation: "Domicile can matter for tax treatment, structure, and legal setup.",
    related: ["nav-currency", "isin", "cusip"],
  },
  {
    slug: "isin",
    label: "ISIN",
    category: "etf",
    shortDefinition: "The international securities identifier for the fund.",
    explanation: "ISIN is a standardized security identifier used across markets.",
    related: ["cusip"],
  },
  {
    slug: "cusip",
    label: "CUSIP",
    category: "etf",
    shortDefinition: "The CUSIP identifier for the fund security.",
    explanation: "CUSIP is a U.S.-oriented identifier used in settlement and reference workflows.",
    related: ["isin"],
  },
  {
    slug: "inception-date",
    label: "Inception Date",
    category: "etf",
    shortDefinition: "The date the ETF launched.",
    explanation: "This helps contextualize how long the fund has been trading and building a record.",
    related: ["assets-under-management"],
  },
  {
    slug: "sector-weights",
    label: "Sector Weights",
    category: "etf",
    shortDefinition: "The ETF's sector allocation breakdown.",
    explanation: "Sector weights show which parts of the economy dominate the fund.",
    aliases: ["Sector"],
    related: ["country-weights", "holdings-count"],
  },
  {
    slug: "country-weights",
    label: "Country Weights",
    category: "etf",
    shortDefinition: "The ETF's country allocation breakdown.",
    explanation: "Country weights show how much geographic concentration the fund carries.",
    aliases: ["Country"],
    related: ["sector-weights", "holdings-count"],
  },
  {
    slug: "portfolio-manager",
    label: "Portfolio",
    category: "workflow",
    shortDefinition: "The operator workflow for organizing active research, candidate, and owned positions.",
    explanation: "Portfolio lets you create saved portfolios that drive the Command queues and the day-to-day workflow detail for tracked equities.",
    interpretation: "It is an operating layer for the app, not a brokerage portfolio tracker.",
    houseRule: "Active research and candidate names feed Command queues. Owned names feed hold, add, and sell-discipline workflows.",
    aliases: ["Portfolio Manager"],
    related: ["candidate", "owned", "holding-state", "target-buy"],
  },
  {
    slug: "holding-state",
    label: "Portfolio Stage",
    category: "workflow",
    shortDefinition: "The workflow label that marks a position as Active Research, Candidate, or Owned.",
    explanation: "Portfolio Stage determines whether the operator workflow treats a name as active research, a candidate setup, or an owned position that needs monitoring discipline.",
    aliases: ["Holding State"],
    related: ["candidate", "owned", "portfolio-manager"],
  },
  {
    slug: "candidate",
    label: "Candidate",
    category: "workflow",
    shortDefinition: "A tracked name that is not yet treated as an owned position.",
    explanation: "Candidate names stay in the operator workflow as monitored setups that may be promoted into an actual buy when price and context improve.",
    related: ["owned", "holding-state", "portfolio-manager"],
  },
  {
    slug: "owned",
    label: "Owned",
    category: "workflow",
    shortDefinition: "A tracked name that is treated as an existing held position.",
    explanation: "Owned names feed hold, add, and start-selling guidance inside Command and Portfolio.",
    related: ["candidate", "holding-state", "portfolio-manager"],
  },
  {
    slug: "recommended-action",
    label: "Recommended Action",
    category: "workflow",
    shortDefinition: "The portfolio-aware action after combining the engine output with holding state.",
    explanation: "The portfolio workflow translates the raw engine action into a practical action for active research, candidate, or owned positions.",
    houseRule: "This is a portfolio-workflow output layered on top of the core engine action.",
    aliases: ["Engine Action"],
    related: ["holding-state", "final-action", "portfolio-manager"],
  },
  {
    slug: "target-buy",
    label: "Target Buy",
    category: "workflow",
    shortDefinition: "The current target buy price used in the portfolio workflow.",
    explanation: "This is the price discipline reference shown in portfolio workflow detail and ticker detail when comparing the current market price to the house entry framework.",
    aliases: ["Buy Target"],
    related: ["buy-tier", "portfolio-manager"],
  },
  {
    slug: "watch-target",
    label: "Watch Target",
    category: "workflow",
    shortDefinition: "The watchlist price level that marks a name as worth tracking more closely for a better setup.",
    explanation: "Watch Target is the softer price-discipline reference used before a full buy threshold is reached. It helps separate names worth monitoring from names already priced attractively enough to buy.",
    houseRule: "This target comes from the house buy-level framework rather than an external market convention.",
    related: ["target-buy", "buy-tier", "valuation-zone"],
  },
  {
    slug: "ticker",
    label: "Ticker",
    category: "workflow",
    shortDefinition: "The short exchange symbol used to identify a company or fund.",
    explanation: "Ticker symbols are the primary navigation and lookup key across the Investments app.",
    related: ["company"],
  },
  {
    slug: "company",
    label: "Company",
    category: "workflow",
    shortDefinition: "The cached company or fund name associated with a ticker.",
    explanation: "This gives the human-readable name behind the symbol shown in research and ETF tables.",
    aliases: ["Fund"],
    related: ["ticker"],
  },
  {
    slug: "warnings",
    label: "Warnings",
    category: "workflow",
    shortDefinition: "The count of attached report or data warnings for the current record.",
    explanation: "Warnings flag data gaps, provider coverage issues, or model caveats that deserve a manual check.",
    related: ["final-action"],
  },
  {
    slug: "buy-levels-warnings",
    label: "Buy Levels Warnings",
    category: "workflow",
    shortDefinition: "Model-specific warnings attached to the buy-level framework.",
    explanation: "Buy Levels Warnings call out missing inputs, invalid scenario rows, or other caveats specific to the entry framework rather than the broader report.",
    related: ["warnings", "entry-framework", "dcf-sanity-check"],
  },
  {
    slug: "risk-review",
    label: "Risk",
    category: "workflow",
    shortDefinition: "The part of the detail view focused on downside framing and fragility.",
    explanation: "Risk gathers narrative watchouts, model warnings, rates context, and downside indicators into one review surface.",
    interpretation: "It is meant to clarify what can go wrong before a buy decision is made.",
    related: ["primary-risk-framing", "top-risks", "downside-indicators"],
  },
  {
    slug: "primary-risk-framing",
    label: "Primary Risk Framing",
    category: "workflow",
    shortDefinition: "The short narrative that summarizes the main reasons the setup may be fragile.",
    explanation: "Primary Risk Framing translates model caveats and business risks into plain-English summary language before the detailed metrics.",
    related: ["risk-review", "top-risks", "model-watchouts"],
  },
  {
    slug: "top-risks",
    label: "Top Risks",
    category: "workflow",
    shortDefinition: "The highest-priority risks the app wants you to keep in mind.",
    explanation: "Top Risks aggregates the strongest watchouts from red flags, warnings, leverage, and valuation pressure into a short list.",
    related: ["primary-risk-framing", "downside-indicators", "warnings"],
  },
  {
    slug: "downside-indicators",
    label: "Downside Indicators",
    category: "workflow",
    shortDefinition: "The compact set of fragility metrics used to pressure-test the setup.",
    explanation: "Downside Indicators surface leverage, liquidity, cash conversion, and hurdle-spread pressure metrics that matter most for downside framing.",
    related: ["risk-review", "debt-fcf", "current-ratio", "fcf-margin", "buy-yld-10y"],
  },
  {
    slug: "model-watchouts",
    label: "Model Watchouts",
    category: "workflow",
    shortDefinition: "The model caveats that deserve manual interpretation before acting.",
    explanation: "Model Watchouts highlight where the displayed outputs are weaker, unavailable, or carrying more caveats than usual.",
    related: ["warnings", "buy-levels-warnings", "reverse-dcf-diagnostics"],
  },
  {
    slug: "reverse-dcf-diagnostics",
    label: "Reverse DCF Diagnostics",
    category: "workflow",
    shortDefinition: "A heuristic read on how much growth the current price seems to imply versus the anchor assumptions.",
    explanation: "Reverse DCF Diagnostics compare the selected scenario and growth anchor against the price-implied readout so stretched assumptions are easier to spot.",
    interpretation: "This is a heuristic check, not a solved implied-growth model.",
    aliases: ["Bear Reverse", "Base Reverse", "Bull Reverse"],
    related: ["growth-anchor", "growth-rate", "dcf-sanity-check", "risk-review"],
  },
  {
    slug: "dcf-lab",
    label: "DCF Lab",
    category: "workflow",
    shortDefinition: "The advanced scenario workspace for changing DCF inputs locally.",
    explanation: "DCF Lab is the model workbench in the detail experience. It owns the advanced horizon, reverse-scenario, and custom what-if controls.",
    houseRule: "Changes in DCF Lab do not rewrite the saved report or the house recommendation.",
    related: ["dcf-sanity-check", "reverse-dcf-diagnostics", "discount-rate", "terminal-growth"],
  },
];

export const INVESTMENTS_GLOSSARY_ENTRIES: GlossaryEntry[] = [...glossaryEntries].sort((left, right) => left.label.localeCompare(right.label));

const glossaryEntryBySlug = new Map(INVESTMENTS_GLOSSARY_ENTRIES.map((entry) => [entry.slug, entry]));
const glossaryEntryByLookup = new Map<string, GlossaryEntry>();

for (const glossaryEntry of INVESTMENTS_GLOSSARY_ENTRIES) {
  glossaryEntryByLookup.set(normalizeGlossaryLookup(glossaryEntry.slug), glossaryEntry);
  glossaryEntryByLookup.set(normalizeGlossaryLookup(glossaryEntry.label), glossaryEntry);
  for (const alias of glossaryEntry.aliases ?? []) {
    glossaryEntryByLookup.set(normalizeGlossaryLookup(alias), glossaryEntry);
  }
}

export function findGlossaryEntryBySlug(slug: string | null | undefined): GlossaryEntry | null {
  if (!slug) {
    return null;
  }
  return glossaryEntryBySlug.get(slug) ?? null;
}

export function findGlossaryEntry(term: string | null | undefined): GlossaryEntry | null {
  if (!term) {
    return null;
  }
  return glossaryEntryByLookup.get(normalizeGlossaryLookup(term)) ?? null;
}

export function glossaryRoute(slug?: string | null): string {
  return slug ? `/investments/reference/glossary/${slug}` : "/investments/reference/glossary";
}

function normalizeGlossaryLookup(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}
